<template>
  <div
    ref="childListContent"
    class="child-list-content flex flex-col"
    :class="{
      'empty-group': isEmptyGroup,
    }"
  >
    <div
      class="child-list-header sticky top-[50px] w-full mb-3 z-10 bg-background-strongest min-h-[47px]"
    >
      <BoardListHeader
        :list-id="props.listId"
        :header-footer-style="{}"
        :expandable="true"
        :show-status-icon-picker="showStatusIconPickerSubGroup || isUnassignedListId(props.listId)"
        :disable-status-icon-picker="true"
        :group-by="boardSubGroupBy"
        :list-cards="listCards"
        :visible="true"
        :is-cards-layout-timeline="false"
        :is-epic-board="false"
        class="mb-1 sticky left-0"
      />
      <HorizontalRule class="ml-3" />
    </div>
    <div
      v-if="listExpanded && !isEmptyGroup"
      class="child-lists flex pl-3"
    >
      <div
        v-for="(parentList, index) in props.mainListIds"
        :key="index"
        v-observe-visibility="{
          callback: (isVisible) => {
            if (isVisible) {
              addVisibleListId(String(parentList));
            } else {
              removeVisibleListId(String(parentList));
            }
          },
        }"
        class="child-list-cards min-w-[274px] flex flex-col overflow-auto"
        :class="{
          'empty-group': isMainListEmpty(parentList),
        }"
      >
        <BoardList
          v-if="
            (subGroupCards?.[parentList]?.[listId]?.cardIds?.length > 0 &&
              (index < 9 || visibleListIds.includes(String(parentList)))) ||
            addCard === `${parentList}-${listId}`
          "
          :ref="`boardList-${props.listId}`"
          :visible="true"
          :list-id="props.listId"
          :parent-list-id="parentList"
          :index="index"
          :large-board="false"
          :max-displayed-lists="maxDisplayedLists"
          :group-by="boardSubGroupBy"
          :expandable="true"
          :position="position"
          :container-width="containerWidth"
          :sprint-disable-drag="true"
          :show-add-card-button="true"
          :show-list-header="false"
          :sub-group-cards="subGroupCards?.[parentList]?.[listId]?.cardIds"
          @hideCreateCardView="addCard = ''"
        />
        <AddNewCardButton
          v-if="
            subGroupCards?.[parentList]?.[listId]?.cardIds?.length === 0 &&
            addCard !== `${parentList}-${listId}` &&
            (index < 9 || visibleListIds.includes(String(parentList)))
          "
          :list-id="listId"
          :parent-list-id="parentList"
          class="mt-1"
          @click="addCard = `${parentList}-${listId}`"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import useSwimlanes from '../../boards/composables/useSwimlanes';
import BoardListHeader from './BoardListHeader.vue';
import BoardList from './BoardList.vue';
import HorizontalRule from '../sections/settings/HorizontalRule.vue';
import AddNewCardButton from './AddNewCardButton.vue';

const { boardSubGroupBy, showStatusIconPickerSubGroup, currentBoardId, hideEmptyGroups } =
  useSwimlanes();

const store = useStore();

const emits = defineEmits(['update:height']);

const addCard = ref('');

const props = defineProps({
  listId: {
    type: String,
    required: true,
  },
  mainListIds: {
    type: Array as () => string[],
    required: true,
  },
  maxDisplayedLists: {
    type: Number,
    default: 4,
  },
  position: {
    type: Number,
    required: false,
    default: 0,
  },
  containerWidth: {
    type: Number,
    required: false,
    default: 0,
  },
  emptyMainLists: {
    type: Array,
    required: false,
    default: () => [],
  },
  allListCards: {
    type: Object,
    required: false,
    default: () => ({}),
  },
  subGroupCards: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const listExpanded = computed(() => {
  return store.getters.getListExpanded(currentBoardId.value, props.listId);
});

const listCards = computed(() => {
  return props.allListCards.subGroups?.[props.listId] ?? [];
});

const isUnassignedListId = (listId: string | number) => {
  return listId === `${currentBoardId.value}-unassigned`;
};

// Check if main list is in the emptyMainLists array
const isMainListEmpty = (mainListId: string) => {
  return props.emptyMainLists.includes(mainListId);
};

const isEmptyGroup = computed(() => {
  return hideEmptyGroups.value && !listCards.value.length;
});

const visibleListIds = ref<string[]>([]);

const addVisibleListId = (listId: string) => {
  if (!visibleListIds.value.includes(listId)) {
    visibleListIds.value.push(listId);
  }
};

const removeVisibleListId = (listId: string) => {
  const index = visibleListIds.value.indexOf(listId);
  if (index > -1) {
    visibleListIds.value.splice(index, 1);
  }
};

const childListContent = ref<HTMLElement | null>(null);
const heightTimeout = ref<NodeJS.Timeout | null>(null);

onMounted(() => {
  heightTimeout.value = setTimeout(() => {
    const height = childListContent.value?.getBoundingClientRect().height;
    emits('update:height', height ?? 0);
    heightTimeout.value = null;
  }, 100);
});
onUnmounted(() => {
  if (heightTimeout.value) {
    clearTimeout(heightTimeout.value);
  }
});
</script>

<style lang="scss">
.child-list-content {
  &.empty-group {
    flex-basis: 0 !important;
    min-height: 0;
    min-width: 0 !important;
    > * {
      display: none;
    }
  }
}
</style>
